/**
 * Weekly league table from league_week_v2(): avatars, language marks, league-win trophies.
 * Tapping a member opens a small sheet with their languages, levels and badges.
 */
import { memo, useEffect, useState } from 'react';
import { fetchLeagueMember, type LeagueMember, type LeagueRow } from '../data/api.ts';
import { cachedLeague, refreshLeague, subscribeLeague } from '../data/league.ts';
import { LANG_MARK, LANG_NAME_RU, badgeGrid } from '../core/achievements.ts';
import { Avatar } from './Avatar.tsx';
import { BadgeMedal } from './Badges.tsx';
import { Icon } from './Icon.tsx';
import { Sheet } from './Sheet.tsx';
import './league.css';

type State = { status: 'loading' } | { status: 'error' } | { status: 'ready'; rows: LeagueRow[] };

const numberFormat = new Intl.NumberFormat('ru-RU');

function initialState(): State {
  const rows = cachedLeague();
  return rows ? { status: 'ready', rows } : { status: 'loading' };
}

/** Member cards are cached for the session: reopening is instant, a refresh follows. */
const memberCache = new Map<string, LeagueMember>();

function MemberSheet({ row, rank, onClose }: { row: LeagueRow; rank: number; onClose: () => void }) {
  const [member, setMember] = useState<LeagueMember | null>(() => memberCache.get(row.user_id) ?? null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    fetchLeagueMember(row.user_id)
      .then((m) => {
        if (!active || !m) return;
        memberCache.set(row.user_id, m);
        setMember(m);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, [row.user_id]);

  const name = row.display_name || 'Без имени';
  const earned = member ? badgeGrid(member.achievements).filter((b) => b.earned) : [];

  return (
    <Sheet title={`Участник лиги: ${name}`} onClose={onClose}>
      <div className="member">
        <div className="member__head">
          <Avatar url={row.avatar_url} eyeColor={row.eye_color} name={name} size={64} />
          <div className="member__who">
            <p className="member__caps">Участник лиги · {rank}-е место</p>
            <h2 className="member__name">
              {name}
              {row.is_me && <span className="t-meta"> (вы)</span>}
            </h2>
            <p className="t-meta">{numberFormat.format(row.xp)} XP за неделю</p>
          </div>
          <button type="button" className="icon-btn member__close" onClick={onClose} aria-label="Закрыть">
            <Icon name="close" />
          </button>
        </div>

        {!member && !failed && <p className="t-meta">Загружаем…</p>}
        {!member && failed && <p className="t-meta">Нет связи — попробуйте позже.</p>}

        {member && (
          <>
            <h3 className="member__section">Языки</h3>
            {member.languages.length === 0 && member.placements.length === 0 ? (
              <p className="t-meta">Пока не начал(а) ни одного урока.</p>
            ) : (
              <ul className="member__langs">
                {member.languages.map((l) => {
                  const tested = member.placements.find((p) => p.lang === l.lang);
                  return (
                    <li key={l.lang} className="member__lang">
                      <span className="member__mark han">{LANG_MARK[l.lang] ?? l.lang.toUpperCase()}</span>
                      <span className="member__lang-text">
                        <strong>
                          {LANG_NAME_RU[l.lang] ?? l.course_title} · {l.level}
                        </strong>
                        <span className="t-meta">
                          <span className="han">{l.book_title}</span>, урок {l.lesson_number} · пройдено уроков: {l.lessons_done}
                          {tested && <> · тест: {tested.level}</>}
                        </span>
                      </span>
                    </li>
                  );
                })}
                {member.placements
                  .filter((p) => !member.languages.some((l) => l.lang === p.lang))
                  .map((p) => (
                    <li key={p.lang} className="member__lang">
                      <span className="member__mark han">{LANG_MARK[p.lang] ?? p.lang.toUpperCase()}</span>
                      <span className="member__lang-text">
                        <strong>
                          {LANG_NAME_RU[p.lang] ?? p.lang} · {p.level}
                        </strong>
                        <span className="t-meta">по тесту на уровень</span>
                      </span>
                    </li>
                  ))}
              </ul>
            )}

            <h3 className="member__section">Достижения {earned.length > 0 && <span className="t-meta">· {earned.length}</span>}</h3>
            {earned.length === 0 ? (
              <p className="t-meta">Пока нет — всё впереди.</p>
            ) : (
              <ul className="member__badges">
                {earned.map((b) => (
                  <li key={b.code} className="member__badge" title={b.howTo}>
                    <BadgeMedal icon={b.icon} earned size={42} />
                    <span>
                      {b.title}
                      {b.count > 1 && <span className="badge-tile__count"> ×{b.count}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Sheet>
  );
}

export const LeagueList = memo(function LeagueList({ limit }: { limit?: number }) {
  // Cached rows render immediately; the background refresh swaps them in when it lands.
  const [state, setState] = useState<State>(initialState);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const unsubscribe = subscribeLeague((rows) => active && setState({ status: 'ready', rows }));
    refreshLeague()
      .then((rows) => active && setState({ status: 'ready', rows }))
      .catch(() => active && setState((s) => (s.status === 'ready' ? s : { status: 'error' })));
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  if (state.status === 'loading') return <p className="empty">Загружаем лигу…</p>;
  if (state.status === 'error') return <p className="empty">Лига появится, когда будет интернет.</p>;
  if (state.rows.length === 0) return <p className="empty">Пока никого нет.</p>;

  const myIndex = state.rows.findIndex((r) => r.is_me);
  const visible = limit ? state.rows.filter((_, i) => i < limit || i === myIndex) : state.rows;
  const openRow = open ? state.rows.find((r) => r.user_id === open) : undefined;

  return (
    <>
      <ol className="league">
        {visible.map((row) => {
          const rank = state.rows.indexOf(row) + 1;
          const name = row.is_me ? 'Вы' : row.display_name || 'Без имени';
          return (
            <li key={row.user_id}>
              <button
                type="button"
                className={`league__row${row.is_me ? ' league__row--me' : ''}${rank === 1 && row.xp > 0 ? ' league__row--first' : ''}`}
                onClick={() => setOpen(row.user_id)}
                aria-label={`${rank}-е место: ${name}, ${row.xp} XP. Открыть карточку`}
              >
                <span className="league__rank">{rank}</span>
                <Avatar url={row.avatar_url} eyeColor={row.eye_color} name={name} size={36} />
                <span className="league__name">
                  {name}
                  {row.wins > 0 && (
                    <span className="league__wins" aria-label={`Побед в лиге: ${row.wins}`}>
                      <Icon name="league" size={14} />
                      {row.wins > 1 ? row.wins : ''}
                    </span>
                  )}
                </span>
                <span className="league__langs" aria-hidden="true">
                  {row.langs.map((l) => (
                    <span key={l} className="league__lang han">
                      {LANG_MARK[l] ?? l.toUpperCase()}
                    </span>
                  ))}
                </span>
                <span className="league__xp">{numberFormat.format(row.xp)} XP</span>
              </button>
            </li>
          );
        })}
      </ol>
      {openRow && (
        <MemberSheet
          row={openRow}
          rank={state.rows.indexOf(openRow) + 1}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
});
